import { useState } from 'react'
import { useSession } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { archiveDocument } from '../api'
import { DOCUMENT_TYPES, DEFAULT_DOCUMENT_TYPE } from '../documentTypes'
import type { DocumentRow } from '../hooks/useDocumentTableQuery'

type DocumentListCardProps = {
  document: DocumentRow
  /** Archived tab → show Restore instead of Archive. */
  archived?: boolean
  /** Open the shared modal in edit mode for this document. */
  onEdit: (document: DocumentRow) => void
}

export function DocumentListCard({
  document,
  archived = false,
  onEdit,
}: DocumentListCardProps) {
  const { toTask } = useAppNavigate()
  const { session } = useSession()
  const queryClient = useQueryClient()
  const [isToggling, setIsToggling] = useState(false)

  const { icon: Icon, colorClass } =
    DOCUMENT_TYPES[document.metadata?.type ?? DEFAULT_DOCUMENT_TYPE] ??
    DOCUMENT_TYPES[DEFAULT_DOCUMENT_TYPE]
  const name = document.name?.trim() || 'Untitled document'
  const description = document.description?.trim()

  const handleNavigate = () =>
    toTask(`/docs/${document.id}`, { replace: false })

  const handleArchiveToggle = async () => {
    setIsToggling(true)
    try {
      const token = await session?.getToken()
      if (!token) throw new Error('No authentication token available')
      await archiveDocument(document.id, !archived, token)
      await queryClient.invalidateQueries({ queryKey: ['documents'] })
      toast.success(archived ? 'Document restored' : 'Document archived')
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : `Failed to ${archived ? 'restore' : 'archive'} document`
      )
    } finally {
      setIsToggling(false)
    }
  }

  return (
    // Stretched-link pattern: the title button's `after:absolute after:inset-0`
    // overlay makes the whole card clickable, while the kebab (relative z-10)
    // stays above the overlay and independently clickable.
    <Card className="relative flex-row items-center justify-between gap-2 px-5 py-5 transition-shadow hover:shadow-lg">
      <div className="flex items-start gap-4 min-w-0">
        <Icon
          size={22}
          strokeWidth={1.5}
          className={`shrink-0 mt-0.5 ${colorClass}`}
        />
        <div className="flex flex-col gap-1 min-w-0">
          <button
            type="button"
            onClick={handleNavigate}
            className="cursor-pointer truncate text-left text-base font-normal text-primary-high-contrast after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:rounded-sm">
            {name}
          </button>
          <p className="truncate text-sm text-muted-foreground">
            {description || 'No description'}
          </p>
        </div>
      </div>
      <div className="relative z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Document actions"
              disabled={isToggling}>
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={handleNavigate}>Open</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onEdit(document)}>
              Edit details
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={handleArchiveToggle}>
              {archived ? 'Restore' : 'Archive'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  )
}
